'use client'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdSearch,
    MdEdit,
    MdInventory,
    MdClose,
    MdFilterList,
    MdWarning,
} from 'react-icons/md'
import {
    getBatches,
    MedicationBatch,
    BatchFilters,
    BATCH_STATUS_LABELS,
    BATCH_STATUS_COLORS,
} from '@/lib/api/stock/batch-api'

export default function BatchesPage() {
    const navigate = useNavigate()
    const [batches, setBatches] = useState<MedicationBatch[]>([])
    const [filteredBatches, setFilteredBatches] = useState<MedicationBatch[]>(
        []
    )
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [filters, setFilters] = useState<BatchFilters>({})
    const [showFilters, setShowFilters] = useState(false)

    useEffect(() => {
        loadBatches()
    }, [filters])

    useEffect(() => {
        if (searchTerm.trim()) {
            const filtered = batches.filter(
                batch =>
                    batch.batchNumber
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    batch.medication?.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
            )
            setFilteredBatches(filtered)
        } else {
            setFilteredBatches(batches)
        }
    }, [searchTerm, batches])

    const loadBatches = async () => {
        setLoading(true)
        try {
            const response = await getBatches(filters)
            if (response.success) {
                setBatches(response.result)
                setFilteredBatches(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar lotes')
            }
        } catch (error) {
            toast.error('Erro ao carregar lotes')
        } finally {
            setLoading(false)
        }
    }

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('pt-BR')
    }

    const isExpiringSoon = (expirationDate: string) => {
        const expDate = new Date(expirationDate)
        const today = new Date()
        const diffDays = Math.ceil(
            (expDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        )
        return diffDays <= 90 && diffDays > 0
    }

    const isExpired = (expirationDate: string) => {
        return new Date(expirationDate) < new Date()
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando lotes...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdInventory className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Lotes
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {batches.length}{' '}
                            {batches.length === 1 ? 'lote' : 'lotes'}{' '}
                            cadastrados
                        </p>
                    </div>
                </div>
                <button
                    className="btn btn-secondary gap-2"
                    onClick={() => navigate('/stock/batches/new')}
                >
                    <MdAdd className="text-xl" />
                    <span className="hidden sm:inline">Entrada</span>
                </button>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por número do lote ou medicamento..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                    <button
                        className="btn btn-outline gap-2"
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <MdFilterList className="text-lg" />
                        Filtros
                    </button>
                </div>

                {showFilters && (
                    <div className="mt-4 pt-4 border-t border-base-300/50 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Status</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={filters.status || ''}
                                onChange={e =>
                                    setFilters({
                                        ...filters,
                                        status:
                                            (e.target.value as any) ||
                                            undefined,
                                    })
                                }
                            >
                                <option value="">Todos</option>
                                {Object.entries(BATCH_STATUS_LABELS).map(
                                    ([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">
                                    Vencendo em (dias)
                                </span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={filters.expiringDays || ''}
                                onChange={e =>
                                    setFilters({
                                        ...filters,
                                        expiringDays: e.target.value
                                            ? parseInt(e.target.value)
                                            : undefined,
                                    })
                                }
                            >
                                <option value="">Todos</option>
                                <option value="30">30 dias</option>
                                <option value="60">60 dias</option>
                                <option value="90">90 dias</option>
                            </select>
                        </div>
                    </div>
                )}
            </div>

            {filteredBatches.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdInventory className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchTerm
                                ? 'Nenhum lote encontrado'
                                : 'Nenhum lote cadastrado'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchTerm
                                ? 'Tente ajustar sua busca'
                                : 'Registre entradas de medicamentos'}
                        </p>
                        {!searchTerm && (
                            <button
                                onClick={() => navigate('/stock/batches/new')}
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Registrar entrada
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="overflow-x-auto bg-base-100 rounded-xl border border-base-300/50">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Lote</th>
                                <th>Medicamento</th>
                                <th>Quantidade</th>
                                <th>Validade</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredBatches.map(batch => (
                                <tr
                                    key={batch.id}
                                    className="hover cursor-pointer"
                                    onClick={() =>
                                        navigate(`/stock/batches/${batch.id}`)
                                    }
                                >
                                    <td>
                                        <div className="font-bold">
                                            {batch.batchNumber}
                                        </div>
                                        {batch.location && (
                                            <div className="text-sm text-base-content/60">
                                                {batch.location}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        <div>{batch.medication?.name}</div>
                                        {batch.medication?.genericName && (
                                            <div className="text-sm text-base-content/60">
                                                {batch.medication.genericName}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        <div className="font-bold">
                                            {batch.currentQuantity}
                                        </div>
                                        <div className="text-sm text-base-content/60">
                                            de {batch.initialQuantity}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="flex items-center gap-2">
                                            {formatDate(batch.expirationDate)}
                                            {isExpired(
                                                batch.expirationDate
                                            ) && (
                                                <span className="badge badge-error badge-sm">
                                                    Vencido
                                                </span>
                                            )}
                                            {isExpiringSoon(
                                                batch.expirationDate
                                            ) && (
                                                <MdWarning className="text-warning" />
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <span
                                            className={`badge ${BATCH_STATUS_COLORS[batch.status]}`}
                                        >
                                            {BATCH_STATUS_LABELS[batch.status]}
                                        </span>
                                    </td>
                                    <td>
                                        <button
                                            onClick={e => {
                                                e.stopPropagation()
                                                navigate(
                                                    `/stock/batches/${batch.id}/editar`
                                                )
                                            }}
                                            className="btn btn-ghost btn-sm"
                                            title="Editar"
                                        >
                                            <MdEdit className="text-lg" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}
